<template>
  <div class="modal-overlay" @click="emit('close')">
    <div class="modal-content card" @click.stop>
      <button class="close-button" @click="emit('close')" aria-label="Close">
        ×
      </button>

      <h2>{{ t('myRole.selectRole') }}</h2>

      <input
        v-model="searchQuery"
        type="text"
        :placeholder="t('common.search')"
        class="search-input"
      />

      <div class="type-filters">
        <button
          :class="['filter-btn', { active: selectedType === null }]"
          @click="selectedType = null"
        >
          {{ t('overview.allRoles') }}
        </button>
        <button
          v-for="type in roleTypes"
          :key="type"
          :class="['filter-btn', `filter-${type}`, { active: selectedType === type }]"
          @click="selectedType = type"
        >
          {{ t(`roleTypes.${type}`) }}
        </button>
      </div>

      <div class="roles-list">
        <button
          v-for="role in filteredRoles"
          :key="role.id"
          :class="['role-item', { selected: currentRoleId === role.id }]"
          @click="selectRole(role.id)"
        >
          <div class="role-item-header">
            <span class="role-name">{{ t(role.nameKey) }}</span>
            <span :class="['badge', `badge-${role.type}`]">
              {{ t(`roleTypes.${role.type}`) }}
            </span>
          </div>
          <p class="role-item-description">{{ t(role.descriptionKey) }}</p>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { RoleType } from '@botc/shared';
import { useRoles } from '../composables/useRoles';

interface Props {
  currentRoleId?: string;
}

defineProps<Props>();

const emit = defineEmits<{
  select: [roleId: string];
  close: [];
}>();

const { t } = useI18n();
const { roles } = useRoles();

const searchQuery = ref('');
const selectedType = ref<RoleType | null>(null);

const roleTypes = [RoleType.TOWNSFOLK, RoleType.OUTSIDER, RoleType.MINION, RoleType.DEMON];

const filteredRoles = computed(() => {
  return roles.value.filter(role => {
    const matchesSearch = searchQuery.value === '' ||
      t(role.nameKey).toLowerCase().includes(searchQuery.value.toLowerCase()) ||
      t(role.descriptionKey).toLowerCase().includes(searchQuery.value.toLowerCase());

    const matchesType = selectedType.value === null || role.type === selectedType.value;

    return matchesSearch && matchesType;
  });
});

function selectRole(roleId: string): void {
  emit('select', roleId);
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: var(--spacing-md);
}

.modal-content {
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.close-button {
  position: absolute;
  top: var(--spacing-md);
  right: var(--spacing-md);
  background: none;
  border: none;
  color: var(--color-text-muted);
  font-size: 2rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color var(--transition-fast);
}

.close-button:hover {
  color: var(--color-text-primary);
}

.modal-content h2 {
  color: var(--color-accent-gold);
  margin-bottom: var(--spacing-lg);
  padding-right: var(--spacing-xl);
}

.search-input {
  margin-bottom: var(--spacing-md);
}

.type-filters {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  margin-bottom: var(--spacing-lg);
}

.filter-btn {
  padding: var(--spacing-xs) var(--spacing-md);
  background-color: var(--color-bg-tertiary);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
  font-size: 0.875rem;
  font-weight: 500;
}

.filter-btn:hover {
  border-color: var(--color-text-muted);
}

.filter-btn.active {
  border-color: var(--color-accent-gold);
  color: var(--color-accent-gold);
}

.filter-townsfolk.active {
  border-color: var(--color-townsfolk);
  color: var(--color-townsfolk);
}

.filter-outsider.active {
  border-color: var(--color-outsider);
  color: var(--color-outsider);
}

.filter-minion.active {
  border-color: var(--color-minion);
  color: var(--color-minion);
}

.filter-demon.active {
  border-color: var(--color-demon);
  color: var(--color-demon);
}

.roles-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.role-item {
  background-color: var(--color-bg-tertiary);
  border: 2px solid transparent;
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  cursor: pointer;
  transition: all var(--transition-fast);
  text-align: left;
}

.role-item:hover {
  border-color: var(--color-text-muted);
  transform: translateX(4px);
}

.role-item.selected {
  border-color: var(--color-accent-gold);
  background-color: var(--color-bg-secondary);
}

.role-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
}

.role-name {
  color: var(--color-text-primary);
  font-weight: 600;
  font-size: 1.125rem;
}

.role-item-description {
  color: var(--color-text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}
</style>
